import { WordPressEvent } from '../services/wordpress';

const DEFAULT_SITE_NAME = 'El Pumarejo';

const stripHtml = (value?: string): string =>
  value
    ? new DOMParser().parseFromString(value, 'text/html').body.textContent?.trim() ?? ''
    : '';

export const getEventTitle = (event: WordPressEvent): string =>
  stripHtml(event.title.rendered);

export const getEventDescription = (event: WordPressEvent): string =>
  stripHtml(event.excerpt?.rendered) || stripHtml(event.content.rendered);

export const getEventImageUrl = (event: WordPressEvent): string =>
  event._embedded?.['wp:featuredmedia']?.[0]?.source_url ||
  '/img/streetview.png';

export const slugify = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const getEventSlug = (event: WordPressEvent): string =>
  event.slug || slugify(getEventTitle(event)) || `event-${event.id}`;

export const getEventPath = (event: WordPressEvent): string =>
  `/events/${getEventSlug(event)}`;

export const parseEventDateTime = (
  dateString?: string,
  timeString?: string
): Date | null => {
  if (!dateString) return null;

  const [day, month, year] = dateString.split('/').map(Number);
  const [hours, minutes] = timeString ? timeString.split(':').map(Number) : [0, 0];

  if ([day, month, year, hours, minutes].some((value) => Number.isNaN(value))) {
    return null;
  }

  return new Date(year, month - 1, day, hours, minutes);
};

export const toIsoStringWithOffset = (date: Date | null): string | undefined => {
  if (!date) return undefined;

  const timezoneOffset = -date.getTimezoneOffset();
  const sign = timezoneOffset >= 0 ? '+' : '-';
  const absoluteOffset = Math.abs(timezoneOffset);
  const offsetHours = Math.floor(absoluteOffset / 60)
    .toString()
    .padStart(2, '0');
  const offsetMinutes = (absoluteOffset % 60).toString().padStart(2, '0');

  return `${date.getFullYear()}-${(date.getMonth() + 1)
    .toString()
    .padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}T${date
    .getHours()
    .toString()
    .padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:00${sign}${offsetHours}:${offsetMinutes}`;
};

export const buildCanonicalUrl = (pathname: string): string => {
  if (/^https?:\/\//i.test(pathname)) {
    return pathname;
  }

  const siteUrl = process.env.REACT_APP_SITE_URL?.replace(/\/$/, '');

  if (siteUrl) {
    return `${siteUrl}${pathname}`;
  }

  return `${window.location.origin}${pathname}`;
};

const formatGoogleCalendarDate = (date: Date): string =>
  `${date.getUTCFullYear()}${(date.getUTCMonth() + 1)
    .toString()
    .padStart(2, '0')}${date.getUTCDate().toString().padStart(2, '0')}T${date
    .getUTCHours()
    .toString()
    .padStart(2, '0')}${date.getUTCMinutes().toString().padStart(2, '0')}${date
    .getUTCSeconds()
    .toString()
    .padStart(2, '0')}Z`;

export const buildGoogleCalendarUrl = (
  event: WordPressEvent,
  canonicalUrl: string
): string | null => {
  const title = getEventTitle(event);
  const details = getEventDescription(event);
  const startDate = parseEventDateTime(event.acf?.event_date, event.acf?.event_time);
  const endDate =
    parseEventDateTime(event.acf?.event_date, event.acf?.closing_time) ||
    (startDate ? new Date(startDate.getTime() + 2 * 60 * 60 * 1000) : null);

  if (!startDate || !endDate) {
    return null;
  }

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${formatGoogleCalendarDate(startDate)}/${formatGoogleCalendarDate(endDate)}`,
    details: details ? `${details}\n\n${canonicalUrl}` : canonicalUrl,
    location: "El Pumarejo, L'Hospitalet de Llobregat",
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

export interface EventStructuredData {
  '@context': 'https://schema.org';
  '@type': 'Event';
  name: string;
  startDate?: string;
  endDate?: string;
  description: string;
  image: string[];
  url: string;
  eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode';
  eventStatus: 'https://schema.org/EventScheduled';
  location: {
    '@type': 'Place';
    name: string;
    address: {
      '@type': 'PostalAddress';
      addressLocality: string;
      addressCountry: string;
    };
  };
  offers?: {
    '@type': 'Offer';
    url: string;
    availability: 'https://schema.org/InStock';
  };
}

export const buildEventStructuredData = (
  event: WordPressEvent,
  canonicalUrl: string
): EventStructuredData => {
  const startDate = parseEventDateTime(event.acf?.event_date, event.acf?.event_time);
  const endDate = parseEventDateTime(
    event.acf?.event_date,
    event.acf?.closing_time || event.acf?.event_time
  );

  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: getEventTitle(event),
    startDate: toIsoStringWithOffset(startDate),
    endDate: toIsoStringWithOffset(endDate),
    description: getEventDescription(event),
    image: [buildCanonicalUrl(getEventImageUrl(event))],
    url: canonicalUrl,
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    location: {
      '@type': 'Place',
      name: DEFAULT_SITE_NAME,
      address: {
        '@type': 'PostalAddress',
        addressLocality: "L'Hospitalet de Llobregat",
        addressCountry: 'ES',
      },
    },
    offers: event.acf?.link_tickets
      ? {
          '@type': 'Offer',
          url: event.acf.link_tickets,
          availability: 'https://schema.org/InStock',
        }
      : undefined,
  };
};
